// Index-aware entity recognition for the JVVNL NLS parser. Builds a normalized
// name → entity lookup from the search index ONCE (memoized on the index ref by
// the caller), and provides a longest-span matcher so multi-word entity names
// like "NEW BADAWAS" win over their single-word substrings. Deterministic,
// O(query length) per match — no AI, no network, no external deps.
//
// Alias-aware (§3): alongside canonical names, an alias map resolves real-world
// operator terminology — abbreviations ("gss bhankari" → BHANKARI), stripped
// voltage prefixes ("aota", "132kv aota" → "132 P AOTA"), and jargon ("dss",
// "grid substation"). Aliases come from `SearchItem.aliases` plus aliases
// generated from the label + category at build time.

import { SearchItem } from "./types";
import { normalizeSearchText } from "./aliases";

export interface EntityLookup {
  /** Normalized entity name → entities. Array because names collide across categories. */
  byName: Map<string, SearchItem[]>;
  /** Normalized real-world alias → entities (abbreviations, jargon, voltage-stripped). */
  byAlias: Map<string, SearchItem[]>;
  /** Longest entity name (or alias) in tokens; bounds the span scan in matchEntity. */
  maxWords: number;
}

/** Lowercase + collapse whitespace, matching how the parser normalizes tokens. */
function normalizeName(label: string): string {
  return normalizeSearchText(label);
}

// Category → jargon words operators use for that entity family. Each becomes an
// alias prefixed to the entity name (e.g. transco "BHANKARI" → "gss bhankari").
const CATEGORY_JARGON: Record<string, string[]> = {
  "Transco Substation": ["gss", "grid substation", "transco substation", "substation"],
  "Discom Substation": ["dss", "discom substation", "substation"],
  Feeder: ["feeder", "fdr", "outgoing feeder"],
  Plant: ["plant", "generator"],
  Circle: ["circle", "zone"],
};

// Voltage prefix tokens that operators drop when referring to a substation by
// its bare location ("132 P AOTA" → "aota"). Folded form from normalizeSearchText
// ("132 kv" → "132kv"); the raw "132 P" pattern is handled by stripping a leading
// voltage + single-letter bay token.
const VOLTAGE_PREFIX_RE =
  /^(?:\d{2,3}\s*kv|\d{2,3}kv|\d{2,3})(?:\s+[a-z])?\s+/i;

/**
 * Generate real-world aliases for an entity from its label + category. Combined
 * with any explicit `item.aliases`. Deterministic and bounded (a few strings
 * per entity), so it adds negligible build cost.
 */
function generatedAliases(item: SearchItem): string[] {
  const out = new Set<string>();
  const name = normalizeName(item.label);
  if (!name) return [];

  // 1) Voltage-stripped bare name: "132kv aota" / "132 p aota" → "aota".
  const stripped = name.replace(VOLTAGE_PREFIX_RE, "").trim();
  if (stripped && stripped !== name) {
    out.add(stripped);
    // Also "<voltage>kv <bare>" forms operators type ("132kv aota", "132 kv aota").
    const vm = name.match(/\b(\d{2,3})\s*kv\b/) || name.match(/^(\d{2,3})\b/);
    if (vm) {
      out.add(`${vm[1]}kv ${stripped}`);
      out.add(`${vm[1]} kv ${stripped}`);
    }
  }

  // 2) Category jargon + name: "gss bhankari", "dss <name>", "feeder <name>".
  const jargon = CATEGORY_JARGON[item.category] ?? [];
  for (const j of jargon) {
    out.add(`${j} ${name}`);
    if (stripped && stripped !== name) out.add(`${j} ${stripped}`);
  }

  return Array.from(out);
}

/**
 * Build the name→entity lookup from the index. Considers only `type === "entity"`
 * items. Also builds a byAlias map (explicit + generated aliases). O(index size);
 * the caller memoizes on the index reference so this runs once per index load.
 */
export function buildEntityLookup(index: SearchItem[]): EntityLookup {
  const byName = new Map<string, SearchItem[]>();
  const byAlias = new Map<string, SearchItem[]>();
  let maxWords = 0;

  const track = (key: string) => {
    const words = key.split(" ").length;
    if (words > maxWords) maxWords = words;
  };
  const add = (map: Map<string, SearchItem[]>, key: string, item: SearchItem) => {
    if (!key) return;
    const arr = map.get(key);
    if (arr) {
      if (!arr.includes(item)) arr.push(item);
    } else map.set(key, [item]);
    track(key);
  };

  for (const item of index) {
    if (item.type !== "entity") continue;
    const name = normalizeName(item.label);
    if (!name) continue;
    add(byName, name, item);

    const aliases = [
      ...(item.aliases ?? []).map(normalizeName),
      ...generatedAliases(item),
    ];
    for (const a of aliases) {
      // Don't let an alias shadow a real canonical name.
      if (byName.has(a)) continue;
      add(byAlias, a, item);
    }
  }
  return { byName, byAlias, maxWords };
}

/**
 * Find an entity by scanning contiguous token spans longest-first (length
 * `maxWords` down to 1), joining each span with a single space. Match order:
 * exact canonical name first, then alias. First hit at the longest span wins,
 * so "new badawas" matches the 2-word entity before the 1-word "badawas", and a
 * canonical name is preferred over an alias of the same length. Returns the
 * canonical name, matched ids, and the [start, end) token span. Null on miss.
 */
export function matchEntity(
  tokens: string[],
  lookup: EntityLookup,
): { name: string; ids: string[]; span: [number, number] } | null {
  const n = tokens.length;
  if (n === 0 || lookup.maxWords === 0) return null;
  const cap = Math.min(lookup.maxWords, n);
  for (let len = cap; len >= 1; len--) {
    for (let start = 0; start + len <= n; start++) {
      const key = tokens.slice(start, start + len).join(" ");
      const hits = lookup.byName.get(key) ?? lookup.byAlias.get(key);
      if (hits && hits.length) {
        // Report the canonical label of the first hit, not the alias the user typed.
        const name = normalizeName(hits[0].label);
        return { name, ids: hits.map((h) => h.id), span: [start, start + len] };
      }
    }
  }
  return null;
}
